import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import warehouseZoneServices from "app/services/warehouse-zone-services";

const EMPTY = { code: "", name: "", color: "#2E7D6F", pick_order: 0 };

// Las zonas de una bodega. Dos campos que se confunden fácil y no son lo mismo:
//
//   Código  entra dentro del código de posición, así que son 1 o 2 letras
//   Nombre  es para la pantalla, y puede ser largo
//
// Por eso "Alto valor" se escribe como código HV y nombre "Alto valor": el código
// viaja impreso en la etiqueta de cada viga y tiene que ser corto.
const ZonesModal = ({ open, onClose, centreId, onChanged }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [zones, setZones] = useState([]);
    const [form, setForm] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = () => {
        if (!centreId) return;
        warehouseZoneServices
            .getZones(centreId)
            .then((data) => setZones(data || []))
            .catch(() => setZones([]));
    };

    useEffect(() => {
        if (!open) return;
        load();
        setForm(EMPTY);
        setEditingId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, centreId]);

    const handleChange = (field) => (event) => setForm({ ...form, [field]: event.target.value });

    const submit = async () => {
        try {
            setSaving(true);
            if (editingId) {
                await warehouseZoneServices.updateZone(editingId, form);
                enqueueSnackbar("Zona actualizada", { variant: "success" });
            } else {
                await warehouseZoneServices.createZone({ ...form, centre_id: centreId });
                enqueueSnackbar("Zona creada", { variant: "success" });
            }
            setForm(EMPTY);
            setEditingId(null);
            load();
            onChanged?.();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudo guardar", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    const edit = (zone) => {
        setEditingId(zone.id);
        setForm({
            code: zone.code,
            name: zone.name,
            color: zone.color || "#2E7D6F",
            pick_order: zone.pick_order,
        });
    };

    const remove = async (zone) => {
        try {
            const result = await warehouseZoneServices.deleteZone(zone.id);
            enqueueSnackbar(result?.message || "Zona eliminada", { variant: "success" });
            load();
            onChanged?.();
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.error || "No se pudo eliminar", { variant: "error" });
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Zonas de la bodega
                <Typography variant="body2" color="text.secondary">
                    Los sectores del plano y el orden en que se caminan
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={4} sm={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Código"
                            value={form.code}
                            onChange={handleChange("code")}
                            placeholder="HV"
                            inputProps={{ maxLength: 2 }}
                            helperText="1-2 letras"
                        />
                    </Grid>
                    <Grid item xs={8} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Nombre"
                            value={form.name}
                            onChange={handleChange("name")}
                            placeholder="Alto valor"
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Orden"
                            value={form.pick_order}
                            onChange={handleChange("pick_order")}
                            helperText="del recorrido"
                        />
                    </Grid>
                    <Grid item xs={6} sm={2}>
                        <TextField
                            fullWidth
                            size="small"
                            type="color"
                            label="Color"
                            value={form.color}
                            onChange={handleChange("color")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <Stack direction="row" spacing={1}>
                            <LoadingButton
                                variant="contained"
                                loading={saving}
                                onClick={submit}
                                disabled={!form.code || !form.name}
                            >
                                {editingId ? "Guardar" : "Agregar"}
                            </LoadingButton>
                            {editingId && (
                                <Button
                                    onClick={() => {
                                        setEditingId(null);
                                        setForm(EMPTY);
                                    }}
                                >
                                    Cancelar
                                </Button>
                            )}
                        </Stack>
                    </Grid>
                </Grid>

                <Alert severity="info" sx={{ mt: 2, py: 0.5 }}>
                    <Typography variant="caption">
                        El código entra dentro del código de cada posición, así que son 1 o 2 letras: <b>A</b>,{" "}
                        <b>DG</b>, <b>HV</b>. El nombre largo va aparte y no viaja en la etiqueta. Menor orden se camina
                        primero.
                    </Typography>
                </Alert>

                <Divider sx={{ my: 3 }} />

                {zones.length === 0 ? (
                    <Alert severity="info">Esta bodega todavía no tiene zonas.</Alert>
                ) : (
                    <Stack spacing={1}>
                        {zones.map((zone) => (
                            <Paper key={zone.id} variant="outlined" sx={{ p: 1.5 }}>
                                <Stack direction="row" alignItems="center" spacing={1.5}>
                                    <Box
                                        sx={{
                                            width: 14,
                                            height: 14,
                                            borderRadius: "3px",
                                            bgcolor: zone.color || "grey.400",
                                            flexShrink: 0,
                                        }}
                                    />
                                    <Chip size="small" label={zone.code} />
                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                        <Typography variant="body2">{zone.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            orden {zone.pick_order} · {zone.bins_count || 0} posiciones
                                        </Typography>
                                    </Box>
                                    <Tooltip title="Editar">
                                        <IconButton size="small" onClick={() => edit(zone)}>
                                            <EditOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Eliminar (las posiciones quedan sin zona)">
                                        <IconButton size="small" onClick={() => remove(zone)}>
                                            <DeleteOutlineOutlinedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Paper>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cerrar</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ZonesModal;
